import React from 'react';
import { Link } from 'react-router-dom';
import {
  MdDeleteOutline,
  MdDownload,
  MdInsertDriveFile,
  MdWarningAmber,
  MdCheckCircle,
  MdOpenInNew,
  MdListAlt,
} from 'react-icons/md';
import { APPRoutes } from '../../Utils/Route';
import SearchableDropdown from '../Subscription/SearchableDropdown';
import { toast } from 'react-toastify';
import BackButton from '../Common/BackButton';
import CustomDatePicker from '../Common/CustomDatePicker';
import DocumentUpload from '../Common/DocumentUpload';
import { useAuth } from '../../Context/AuthContext';
import { payerOptions } from './StediSelects';
import { downloadBulkTemplate } from '../../Services/Claim360Service';
import { importSheetRows, payerKey, templateAliases, templateColumns } from '../../Utils/stediUtils';
import DataGrid from '../Table/DataGrid';
import { PANEL_STYLE } from './StediResultParts';
import { STATUS_STYLE } from './StediViewParts';

const FIELD_HEIGHT = 40;

const plural = (count, noun) => `${count} ${noun}${count === 1 ? '' : 's'}`;

const fieldError = (formik, name) => formik.touched[name] && formik.errors[name];

// Status pills of the import preview: the status colours with a matching outline.
const PILL = {
  danger: { ...STATUS_STYLE.danger, border: '1px solid var(--hc-status-danger-text)' },
  success: { ...STATUS_STYLE.success, border: '1px solid var(--hc-status-success-text)' },
};

const RequiredMark = () => <span className="text-danger ms-1">*</span>;

// Page frame shared by the eligibility and claim status forms: header, single/bulk switch, footer actions.
export function StediFormShell({
  title,
  subtitle,
  onBack,
  tabs,
  activeTab,
  onTabChange,
  loading,
  canSave,
  deniedMessage,
  onSubmit,
  isSubmitting,
  submitLabel,
  submittingLabel = 'Checking...',
  children,
}) {
  if (loading) {
    return (
      <div className="spinner position-absolute d-flex align-items-center justify-content-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!canSave) {
    return (
      <div className="container-fluid py-4">
        <div className="p-4 bg-white rounded-4 shadow-sm">
          <h5>Permission denied</h5>
          <p className="text-muted">{deniedMessage}</p>
          <BackButton onClick={onBack} />
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid p-0">
      <form noValidate onSubmit={onSubmit} className="rounded-4 bg-white p-3 p-md-4 shadow-lg border-0">
        <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3 pb-4 border-bottom">
          <div className="d-flex flex-column flex-sm-row align-items-sm-center gap-3" style={{ minWidth: 0 }}>
            <BackButton onClick={onBack} className="align-self-start align-self-sm-center flex-shrink-0" />
            <div style={{ minWidth: 0 }}>
              <h3 className="fw-bold mb-1 hc-text-primary" style={{ fontSize: 'clamp(1.25rem, 2.4vw, 1.6rem)' }}>{title}</h3>
              <div className="small text-muted">{subtitle}</div>
            </div>
          </div>

          {tabs && (
            <div className="d-flex gap-1 p-1 rounded-pill border bg-light flex-shrink-0" role="tablist">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  className={`btn btn-sm rounded-pill fw-semibold flex-fill px-3 py-2 text-nowrap border-0 ${
                    activeTab === tab.id ? 'hc-bg-primary text-white shadow-sm' : 'text-secondary'
                  }`}
                  onClick={() => activeTab !== tab.id && onTabChange(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {children}

        <div className="d-flex flex-column-reverse flex-sm-row justify-content-sm-end gap-2 pt-4">
          <button type="button" className="btn hc-btn-prev rounded-3 px-4 py-2 fw-semibold" onClick={onBack} disabled={isSubmitting}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn hc-bg-primary text-white rounded-3 px-4 py-2 fw-semibold shadow-sm d-inline-flex align-items-center justify-content-center gap-2"
            disabled={isSubmitting}
          >
            {isSubmitting && <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />}
            {isSubmitting ? submittingLabel : submitLabel}
          </button>
        </div>
      </form>
    </div>
  );
}

// One form section: icon heading with its description, fields below. Children are laid out in a Bootstrap row.
export function FormSection({ icon: Icon, title, subtitle, children }) {
  return (
    <section className="py-4 border-bottom">
      <div className="d-flex align-items-start gap-3 mb-3">
        <span
          className="d-inline-flex align-items-center justify-content-center rounded-3 hc-bg-primary text-white flex-shrink-0"
          style={{ width: 36, height: 36 }}
        >
          <Icon size={18} />
        </span>
        <div style={{ minWidth: 0 }}>
          <h6 className="fw-bold hc-text-primary mb-1">{title}</h6>
          {subtitle && <div className="small text-muted">{subtitle}</div>}
        </div>
      </div>
      <div className="row g-3">{children}</div>
    </section>
  );
}

export function TextInput({ formik, name, label, col = 'col-12 col-md-6', required = true, ...inputProps }) {
  const error = fieldError(formik, name);
  return (
    <div className={col}>
      <label className="form-label fw-semibold mb-2" htmlFor={name}>
        {label}
        {required && <RequiredMark />}
      </label>
      <input
        id={name}
        type="text"
        name={name}
        className={`form-control rounded-3 ${error ? 'is-invalid' : ''}`}
        style={{ height: FIELD_HEIGHT }}
        value={formik.values[name]}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        {...inputProps}
      />
      {error && <div className="invalid-feedback">{error}</div>}
    </div>
  );
}

// CustomDatePicker shows its own Formik error as helper text, so only the label is added here.
export function DateInput({ formik, name, label, col = 'col-12 col-md-6', required = true, ...pickerProps }) {
  return (
    <div className={col}>
      <label className="form-label fw-semibold mb-2">
        {label}
        {required && <RequiredMark />}
      </label>
      <CustomDatePicker formik={formik} name={name} {...pickerProps} />
    </div>
  );
}

// Excel import for bulk checks: template download, drop zone, imported-file summary and a preview of the
// rows. `kind` picks the template ("eligibility" or "claim-status"); rows come back as
// { rowNumber, <template field>: text, mappedPayerId? } together with the file itself, which is uploaded as is.
// The form owns both (`rows`, `file`), so nothing is lost if this component remounts.
// Insurance names that match no payer (or several) are flagged; the payer picked for such a name is sent
// with the upload as insuranceMappings, so the user's own file is never rewritten.
export function BulkSubscriberImport({
  kind,
  rows,
  file,
  onRowsChange,
  defaultPayerId,
  payers = [],
  error,
  noun = 'patient',
  serverErrors = [],
}) {
  const { token } = useAuth();
  const [replacements, setReplacements] = React.useState({});
  const [showOnlyFlagged, setShowOnlyFlagged] = React.useState(false);
  const fileName = file?.name ?? '';

  const columns = templateColumns(kind);
  const required = columns.filter((column) => column.required);
  const optional = columns.filter((column) => !column.required);

  // Payer IDs per normalized insurance name or payer ID, as the backend matches them; more than one is ambiguous.
  const payerIdsByKey = React.useMemo(() => {
    const map = new Map();
    const add = (key, payerId) => {
      if (!key || !payerId) return;
      if (!map.has(key)) map.set(key, new Set());
      map.get(key).add(payerId);
    };
    payers.forEach(({ name, primaryPayerId, aliases = [] }) => {
      [name, primaryPayerId, ...aliases].forEach((value) => add(payerKey(value), primaryPayerId));
    });
    return map;
  }, [payers]);

  const options = React.useMemo(() => payerOptions(payers), [payers]);

  // Rows whose insurance name the upload would reject, grouped by that name.
  const { unrecognizedRows, distinctUnrecognized } = React.useMemo(() => {
    const serverFlagged = new Set(serverErrors.map(({ rowNumber }) => Number(rowNumber)));
    const flagged = rows.filter((row) => row.insuranceName && !row.mappedPayerId && (
      serverFlagged.has(Number(row.rowNumber))
      || (payers.length > 0 && payerIdsByKey.get(payerKey(row.insuranceName))?.size !== 1)
    ));
    const groups = new Map();
    flagged.forEach((row) => {
      const group = groups.get(row.insuranceName) ?? { name: row.insuranceName, count: 0, rowNumbers: [] };
      group.count += 1;
      group.rowNumbers.push(row.rowNumber);
      groups.set(row.insuranceName, group);
    });
    return { unrecognizedRows: flagged, distinctUnrecognized: [...groups.values()] };
  }, [rows, serverErrors, payers.length, payerIdsByKey]);

  // Show just the flagged rows while there are any.
  React.useEffect(() => {
    setShowOnlyFlagged(unrecognizedRows.length > 0);
  }, [unrecognizedRows.length]);

  const unrecognizedRowNumberSet = React.useMemo(
    () => new Set(unrecognizedRows.map((row) => Number(row.rowNumber))),
    [unrecognizedRows],
  );

  const payerName = (payerId) => payers.find((payer) => payer.primaryPayerId === payerId)?.name || payerId;

  // Maps the rows that pass `matches` to a payer; returns how many there were.
  const mapRows = (matches, payerId) => {
    onRowsChange(rows.map((row) => (matches(row) ? { ...row, mappedPayerId: payerId } : row)), file);
    return rows.filter(matches).length;
  };

  const handleApplyReplacement = (name) => {
    const payerId = replacements[name];
    if (!payerId) return;
    const count = mapRows((row) => row.insuranceName === name, payerId);
    setReplacements(({ [name]: _removed, ...rest }) => rest);
    toast.success(`"${name}" will be checked as ${payerName(payerId)} in ${plural(count, 'row')}.`);
  };

  const handleReplaceAllWithDefault = () => {
    if (!defaultPayerId) return;
    const count = mapRows((row) => unrecognizedRowNumberSet.has(Number(row.rowNumber)), defaultPayerId);
    toast.success(`${plural(count, 'row')} will be checked as ${payerName(defaultPayerId)}.`);
  };

  const handleTemplate = () => {
    downloadBulkTemplate(kind, token).catch((downloadError) => toast.error(downloadError.message));
  };

  const handleFile = async (picked) => {
    if (!picked) return;
    try {
      const importedRows = await importSheetRows(picked, templateAliases(kind));
      if (!importedRows.length) {
        toast.error('The file does not contain any data rows.');
        return;
      }

      const invalidRow = importedRows.find((row) => required.some(({ field }) => !row[field]));
      if (invalidRow) {
        toast.error(`Row ${invalidRow.rowNumber} is missing a value in: ${required.map(({ header }) => header).join(', ')}.`);
        return;
      }
      const invalidNpi = importedRows.find((row) => row.npi && !/^\d{10}$/.test(String(row.npi).trim()));
      if (invalidNpi) {
        toast.error(`Row ${invalidNpi.rowNumber} has an invalid Billing Provider NPI; it must be 10 digits.`);
        return;
      }

      onRowsChange(importedRows, picked);
      setReplacements({});
      toast.success(`${plural(importedRows.length, noun)} loaded.`);
    } catch (importError) {
      console.error('Failed to import file:', importError);
      toast.error('Unable to read this file. Please upload a valid .xlsx or .csv file.');
    }
  };

  const clear = () => {
    onRowsChange([], null);
    setReplacements({});
  };

  const defaultPayerObj = payers.find((payer) => payer.primaryPayerId === defaultPayerId);

  // The insurance cell: flagged, mapped to a payer on this screen, the form default, or as typed.
  const insuranceCell = (row) => {
    if (unrecognizedRowNumberSet.has(Number(row.rowNumber))) {
      return <span className="badge px-2 py-1 d-inline-flex align-items-center gap-1" style={PILL.danger}><MdWarningAmber size={14} /> {row.insuranceName}</span>;
    }
    if (row.mappedPayerId) {
      return <span className="badge px-2 py-1 d-inline-flex align-items-center gap-1" style={PILL.success}><MdCheckCircle size={14} /> {row.insuranceName} → {payerName(row.mappedPayerId)}</span>;
    }
    if (!row.insuranceName) {
      return <span style={{ color: 'var(--hc-text-faint)' }}>{defaultPayerObj ? `${defaultPayerObj.name} (Default)` : 'Form default'}</span>;
    }
    return row.insuranceName;
  };

  const previewColumns = [
    { id: 'rowNumber', label: 'Row', minWidth: 70, format: (value) => `#${value}` },
    ...columns.map(({ field, header }) => ({
      id: field,
      label: header,
      minWidth: field === 'insuranceName' ? 220 : 130,
      format: field === 'insuranceName' ? (_value, row) => insuranceCell(row) : (value) => value || '-',
    })),
  ];

  return (
    <div className="col-12">
      <div className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-2 mb-3 p-3 rounded-3 border" style={PANEL_STYLE}>
        <div className="small" style={{ color: 'var(--hc-body-text)' }}>
          Columns: <span className="fw-semibold">{required.map(({ header }) => header).join(', ')}</span>
          {optional.length > 0 && (
            <>, optional <span className="fw-semibold">{optional.map(({ header }) => header).join(', ')}</span></>
          )}
          . Rows without their own value use the defaults picked on this form.
        </div>
        <button
          type="button"
          className="btn btn-sm hc-btn-prev rounded-3 fw-semibold d-inline-flex align-items-center justify-content-center gap-1 flex-shrink-0 text-nowrap"
          onClick={handleTemplate}
        >
          <MdDownload size={17} /> Download template
        </button>
      </div>

      {/* Remount on each file so the same file can be picked again after clearing. */}
      <DocumentUpload
        key={fileName}
        label="Patient File"
        title="Drop the filled-in template here"
        accept=".xlsx,.csv"
        value={fileName}
        onChange={handleFile}
      />
      {error && <div className="small text-danger mb-3" role="alert">{error}</div>}

      {distinctUnrecognized.length > 0 && (
        <div className="rounded-4 p-3 p-md-4 mb-3 border" style={{ ...STATUS_STYLE.danger, borderColor: 'var(--hc-status-danger-text)' }}>
          <div className="d-flex flex-column flex-md-row align-items-md-start justify-content-between gap-3 mb-3">
            <div className="d-flex align-items-start gap-2">
              <MdWarningAmber size={24} className="flex-shrink-0" />
              <div>
                <div className="fw-bold">
                  {plural(unrecognizedRows.length, 'row')} {unrecognizedRows.length === 1 ? 'has an' : 'have an'} unrecognized insurance name
                </div>
                <div className="small" style={{ color: 'var(--hc-body-text)' }}>
                  Pick the payer for each name below, or look the name up in the Insurance List. Your file is uploaded unchanged.
                </div>
              </div>
            </div>
            <div className="d-flex flex-wrap gap-2 flex-shrink-0">
              <Link
                to={APPRoutes.INSURANCE_LIST}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-sm hc-btn-prev fw-semibold d-inline-flex align-items-center gap-1 text-nowrap rounded-3"
              >
                <MdListAlt size={16} /> Insurance List <MdOpenInNew size={13} />
              </Link>
              {defaultPayerId && (
                <button
                  type="button"
                  className="btn btn-sm hc-btn-prev fw-semibold text-nowrap rounded-3"
                  onClick={handleReplaceAllWithDefault}
                >
                  Use {defaultPayerObj?.name || defaultPayerId} for all
                </button>
              )}
            </div>
          </div>

          <div className="d-flex flex-column gap-2">
            {distinctUnrecognized.map(({ name, count, rowNumbers }) => (
              <div
                key={name}
                className="p-3 rounded-3 border d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-2"
                style={{ background: 'var(--hc-surface)', borderColor: 'var(--hc-surface-border)' }}
              >
                <div style={{ minWidth: 0 }}>
                  <div className="fw-semibold text-break" style={{ color: 'var(--hc-text-title)' }}>"{name}"</div>
                  <div className="small" style={{ color: 'var(--hc-text-muted)' }}>
                    {count === 1 ? `Row ${rowNumbers[0]}` : `${count} rows: ${rowNumbers.slice(0, 8).join(', ')}${rowNumbers.length > 8 ? '…' : ''}`}
                  </div>
                </div>
                <div className="d-flex align-items-center gap-2 w-100" style={{ maxWidth: 440 }}>
                  <div className="flex-grow-1" style={{ minWidth: 0 }}>
                    <SearchableDropdown
                      options={options}
                      value={replacements[name] || ''}
                      onChange={(value) => setReplacements((previous) => ({ ...previous, [name]: value }))}
                      placeholder="Search payer by name or ID"
                      pageSize={50}
                    />
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm hc-bg-primary text-white rounded-3 text-nowrap fw-semibold px-3"
                    style={{ height: FIELD_HEIGHT }}
                    disabled={!replacements[name]}
                    onClick={() => handleApplyReplacement(name)}
                  >
                    Apply ({count})
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {rows.length > 0 && (
        <div className="rounded-4 border" style={{ borderColor: 'var(--hc-surface-border)', background: 'var(--hc-surface)' }}>
          <div className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-2 px-3 py-2 border-bottom" style={PANEL_STYLE}>
            <div className="d-flex align-items-center gap-2 small flex-wrap" style={{ minWidth: 0 }}>
              <MdInsertDriveFile size={18} className="hc-text-primary flex-shrink-0" />
              <span className="fw-semibold text-truncate" style={{ color: 'var(--hc-text-title)' }}>{fileName}</span>
              <span className="badge rounded-pill border" style={{ ...PANEL_STYLE, color: 'var(--hc-body-text)' }}>{plural(rows.length, 'row')}</span>
              {unrecognizedRows.length > 0 ? (
                <span className="badge rounded-pill d-inline-flex align-items-center gap-1" style={PILL.danger}>
                  <MdWarningAmber size={13} /> {unrecognizedRows.length} flagged
                </span>
              ) : (
                <span className="badge rounded-pill d-inline-flex align-items-center gap-1" style={PILL.success}>
                  <MdCheckCircle size={13} /> All payers recognized
                </span>
              )}
            </div>
            <div className="d-flex align-items-center gap-2 flex-shrink-0">
              {unrecognizedRows.length > 0 && (
                <div className="d-flex gap-1 p-1 rounded-pill border" role="tablist" style={PANEL_STYLE}>
                  {[[false, `All (${rows.length})`], [true, `Flagged (${unrecognizedRows.length})`]].map(([value, label]) => (
                    <button
                      key={label}
                      type="button"
                      role="tab"
                      aria-selected={showOnlyFlagged === value}
                      className={`btn btn-sm rounded-pill fw-semibold px-3 border-0 ${showOnlyFlagged === value ? 'hc-bg-primary text-white' : 'text-secondary'}`}
                      onClick={() => setShowOnlyFlagged(value)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
              <button type="button" className="btn btn-sm btn-outline-danger rounded-3 d-inline-flex align-items-center gap-1" onClick={clear}>
                <MdDeleteOutline size={16} />
                <span className="d-none d-sm-inline">Clear</span>
              </button>
            </div>
          </div>
          <div className="p-2">
            {/* Remount when the filter changes so the grid starts again from its first page. */}
            <DataGrid
              key={String(showOnlyFlagged)}
              columns={previewColumns}
              rows={showOnlyFlagged ? unrecognizedRows : rows}
              defaultRowsPerPage={10}
              getRowId={(row) => row.rowNumber}
            />
          </div>
        </div>
      )}
    </div>
  );
}
